import type { FC } from "react";

import { BasicModal, Button } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import TimeBox from "app/components/React/TrialBanner/DiscountBanner/TimeBox/TimeBox";
import { useTimeLeft } from "app/components/timers/hooks/useTimeLeft";
import { getFormattedTimeLeft } from "app/components/timers/utils/helpers";
import { translate } from "app/filters/i18nFilter";
import { AssetsService } from "app/services/AssetsService";
import { allTrackers } from "app/services/track/track";
import { goToPackagesPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToPackagesPage";

import {
    CloseButton,
    DiscountText,
    Disclaimer,
    PopupContainer,
    TimerContainer,
    Subtitle,
    Title,
    Heading,
    GiftImage,
    modalStyles,
    theme,
} from "./StyledDiscountPopup";

interface DiscountPopupProps {
    setShouldShowDiscountPopup: (shouldShowDiscountPopup: boolean) => void;
    discountEndTime?: string;
}

export const DiscountPopup: FC<DiscountPopupProps> = ({ setShouldShowDiscountPopup, discountEndTime }) => {
    const discountEndDateFromSettings = swSettings.user?.customData?.DiscountEndDate;

    const timeLeft = useTimeLeft({ discountEndDateFromSettings, discountEndTime });

    const getEventName = () => {
        const formattedTimeLeft = getFormattedTimeLeft({ discountEndDateFromSettings, discountEndTime });
        return `Discount Banner//${formattedTimeLeft}`;
    };

    const handleClose = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "close", getEventName());
        setShouldShowDiscountPopup(false);
    };

    const handleClick = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", getEventName());
        goToPackagesPage();
    };

    return (
        <BasicModal
            isOpen={true}
            closeIconConfig={{ show: false }}
            customStyles={modalStyles}
            shouldCloseOnOverlayClick={false}
        >
            <PopupContainer>
                <CloseButton
                    onClick={handleClose}
                    type="flat"
                    iconName="close"
                    iconSize="xs"
                />
                <Title>{translate("discount.popup.after_trial.title")}</Title>
                <Heading>{translate("discount.popup.after_trial.heading")}</Heading>
                <GiftImage src={AssetsService.assetUrl(theme.giftImageIcon)} />
                <Subtitle>{translate("discount.popup.after_trial.subtitle")}</Subtitle>
                <DiscountText>{translate("discount.popup.after_trial.discount.text")}</DiscountText>
                <TimerContainer>
                    <TimeBox
                        time={timeLeft.days}
                        label={translate("discount.popup.trial.time.day")}
                    />
                    <TimeBox
                        time={timeLeft.hours}
                        label={translate("discount.popup.trial.time.hours")}
                    />
                    <TimeBox
                        time={timeLeft.minutes}
                        label={translate("discount.popup.trial.time.min")}
                    />
                    <TimeBox
                        time={timeLeft.seconds}
                        label={translate("discount.popup.trial.time.sec")}
                    />
                </TimerContainer>
                <Button
                    type={theme.CTAButtonType}
                    onClick={handleClick}
                >
                    {translate("discount.popup.after_trial.button")}
                </Button>
                <Disclaimer>{translate("discount.popup.after_trial.disclaimer")}</Disclaimer>
            </PopupContainer>
        </BasicModal>
    );
};
